// Active simulation of the opened client: allocation, three scenario series,
// four indicators per scenario, and the selected scenario.
//
// Two sources feed the very same shape. The local preview, computed with
// @horizon/coeur-calcul, is available as soon as the rows are loaded and makes
// the scenario switch instantaneous, far below the one second budget. The
// authoritative figures come from the `simuler` Edge Function, which also
// persists the projections used by the PDF report (RG-03); they replace the
// preview as soon as they arrive.
import { defineStore } from 'pinia';
import { computed, ref, shallowRef } from 'vue';
import {
  ErreurHorizon,
  allocations,
  rendementsActifs,
  simulationActive,
  simuler,
  toErreur,
  toutesEcheances,
} from '@horizon/api-client';
import { HYPOTHESES_DEFAUT, SCENARIOS, simulerTousScenarios } from '@horizon/coeur-calcul';
import type { CodeScenario, ResultatSimulation } from '@horizon/coeur-calcul';
import type { Allocation, Echeance, Simulation } from '@horizon/types';
import {
  construireParams,
  debutSimulation,
  moisEntre,
  cumulKiloEuros,
  normaliserSortieServeur,
  rendementsNormalises,
  versKiloEuros,
  versPointsAnnuels,
} from '@/utils/apercuSimulation';
import type { KpisScenario, PointAnnuel } from '@/utils/apercuSimulation';

/** Weights of the four asset classes, in percent, as expected by HzDonutAllocation. */
export interface PoidsAllocation {
  readonly actions: number;
  readonly obligations: number;
  readonly fonds_euros: number;
  readonly liquidites: number;
}

export const useSimulationStore = defineStore('simulation', () => {
  const simulation = ref<Simulation | null>(null);
  const lignesAllocation = ref<readonly Allocation[]>([]);
  const echeances = ref<readonly Echeance[]>([]);
  const rendements = ref<Record<CodeScenario, number>>({ ...HYPOTHESES_DEFAUT.scenarios });
  const scenario = ref<CodeScenario>('equilibre');

  /** Local preview; not reactive in depth, it is replaced wholesale on each run. */
  const apercu = shallowRef<ResultatSimulation | null>(null);
  const seriesServeur = shallowRef<Record<CodeScenario, readonly PointAnnuel[]> | null>(null);
  const kpisServeur = shallowRef<Record<CodeScenario, KpisScenario> | null>(null);

  const chargement = ref(false);
  const recalculEnCours = ref(false);
  const erreur = ref<ErreurHorizon | null>(null);
  const hypothesesVersion = ref<number>(HYPOTHESES_DEFAUT.version);

  let jeton = 0;
  let dernierClientId: string | null = null;

  const erreurReseau = computed<ErreurHorizon | null>(() =>
    erreur.value !== null && erreur.value.reseau ? erreur.value : null,
  );
  const erreurServeur = computed<ErreurHorizon | null>(() =>
    erreur.value !== null && !erreur.value.reseau ? erreur.value : null,
  );
  /** No active simulation for this client: the screen shows its empty state with an action. */
  const estVide = computed(
    () => !chargement.value && erreur.value === null && simulation.value === null,
  );

  const series = computed<Record<CodeScenario, readonly PointAnnuel[]>>(() => {
    if (seriesServeur.value !== null) {
      return seriesServeur.value;
    }
    const sortie = {} as Record<CodeScenario, readonly PointAnnuel[]>;
    const resultat = apercu.value;
    for (const code of SCENARIOS) {
      sortie[code] = resultat === null ? [] : versPointsAnnuels(resultat.pointsAnnuels[code] ?? []);
    }
    return sortie;
  });

  const kpis = computed<Record<CodeScenario, KpisScenario> | null>(() => {
    if (kpisServeur.value !== null) {
      return kpisServeur.value;
    }
    const resultat = apercu.value;
    if (resultat === null) {
      return null;
    }
    const sortie = {} as Record<CodeScenario, KpisScenario>;
    for (const code of SCENARIOS) {
      const valeur = resultat.kpis[code];
      if (valeur !== undefined) {
        // The local preview never carries the published variation chips.
        sortie[code] = { ...valeur, variationCapitalPct: null, variationRendementPt: null };
      }
    }
    return sortie;
  });

  const pointsScenario = computed<readonly PointAnnuel[]>(() => series.value[scenario.value] ?? []);
  /** Zero based index of the current year on the A1..AN axis, pinned by the chart. */
  const anneeCouranteIndex = computed<number | undefined>(() => {
    const sim = simulation.value;
    if (sim === null) {
      return undefined;
    }
    const mois = moisEntre(debutSimulation(sim), new Date());
    if (mois <= 0) {
      return undefined;
    }
    return Math.min(Math.floor((mois - 1) / 12), sim.horizon_annees - 1);
  });
  const kpisScenario = computed<KpisScenario | null>(() => kpis.value?.[scenario.value] ?? null);
  /** Capital of the selected scenario in k euros, A1..AN. */
  const capitalKe = computed(() => versKiloEuros(pointsScenario.value));
  /** Cumulated contributions in k euros, the dashed reference series. */
  const versementsKe = computed(() => cumulKiloEuros(pointsScenario.value));
  /** The three series in k euros, so the chart scale never moves on a switch. */
  const toutesSeriesKe = computed<readonly (readonly number[])[]>(() =>
    SCENARIOS.map((code) => versKiloEuros(series.value[code] ?? [])),
  );

  const poidsAllocation = computed<PoidsAllocation>(() => {
    let actions = 0;
    let obligations = 0;
    let fondsEuros = 0;
    let liquidites = 0;
    for (const ligne of lignesAllocation.value) {
      if (ligne.classe === 'actions') {
        actions = ligne.poids;
      } else if (ligne.classe === 'obligations') {
        obligations = ligne.poids;
      } else if (ligne.classe === 'fonds_euros') {
        fondsEuros = ligne.poids;
      } else {
        liquidites = ligne.poids;
      }
    }
    return { actions, obligations, fonds_euros: fondsEuros, liquidites };
  });

  /** Recompute the local preview. Synchronous and cheap: pure arithmetic, no network. */
  function rafraichirApercu(): void {
    const active = simulation.value;
    if (active === null) {
      apercu.value = null;
      return;
    }
    const params = construireParams(active, echeances.value, rendements.value);
    apercu.value = simulerTousScenarios(params, active.hypotheses_version);
  }

  /** Load the active simulation of a client and everything the screen needs. */
  async function charger(clientId: string): Promise<void> {
    dernierClientId = clientId;
    jeton += 1;
    const courant = jeton;
    chargement.value = true;
    erreur.value = null;
    seriesServeur.value = null;
    kpisServeur.value = null;
    try {
      const active = await simulationActive(clientId);
      if (courant !== jeton) {
        return;
      }
      simulation.value = active;
      if (active === null) {
        lignesAllocation.value = [];
        echeances.value = [];
        apercu.value = null;
        return;
      }
      scenario.value = active.scenario_defaut;
      hypothesesVersion.value = active.hypotheses_version;
      const [poids, lignes, bruts] = await Promise.all([
        allocations(active.id),
        toutesEcheances(active.id),
        rendementsActifs(active.organisation_id),
      ]);
      if (courant !== jeton) {
        return;
      }
      lignesAllocation.value = poids;
      echeances.value = lignes;
      rendements.value = rendementsNormalises(bruts);
      rafraichirApercu();
    } catch (cause) {
      if (courant !== jeton) {
        return;
      }
      erreur.value = toErreur(cause);
      apercu.value = null;
    } finally {
      if (courant === jeton) {
        chargement.value = false;
      }
    }
  }

  /**
   * Ask the Edge Function for the authoritative projections. The preview stays
   * on screen while the call runs, so the advisor never sees an empty chart.
   */
  async function recalculer(): Promise<boolean> {
    const active = simulation.value;
    if (active === null || recalculEnCours.value) {
      return false;
    }
    recalculEnCours.value = true;
    erreur.value = null;
    try {
      const sortie = await simuler(active.id);
      const normalise = normaliserSortieServeur(sortie);
      seriesServeur.value = normalise.series;
      kpisServeur.value = normalise.kpis;
      hypothesesVersion.value = normalise.hypothesesVersion;
      return true;
    } catch (cause) {
      erreur.value = toErreur(cause);
      return false;
    } finally {
      recalculEnCours.value = false;
    }
  }

  /** Select a scenario. Answers from the already computed series, hence instantly. */
  function choisirScenario(code: CodeScenario): void {
    scenario.value = code;
  }

  /** Replay the last load, wired to the retry action of the network banner. */
  async function reessayer(): Promise<void> {
    if (dernierClientId === null) {
      return;
    }
    await charger(dernierClientId);
  }

  function reinitialiser(): void {
    jeton += 1;
    dernierClientId = null;
    simulation.value = null;
    lignesAllocation.value = [];
    echeances.value = [];
    apercu.value = null;
    seriesServeur.value = null;
    kpisServeur.value = null;
    erreur.value = null;
    chargement.value = false;
    recalculEnCours.value = false;
  }

  return {
    simulation,
    lignesAllocation,
    echeances,
    rendements,
    scenario,
    chargement,
    recalculEnCours,
    erreur,
    hypothesesVersion,
    erreurReseau,
    erreurServeur,
    estVide,
    series,
    kpis,
    anneeCouranteIndex,
    pointsScenario,
    kpisScenario,
    capitalKe,
    versementsKe,
    toutesSeriesKe,
    poidsAllocation,
    charger,
    recalculer,
    rafraichirApercu,
    choisirScenario,
    reessayer,
    reinitialiser,
  };
});
